/**
 * Reads one public-data path again and again until stopped.
 *
 * This file has no imports, so `node --test` can run its tests with no build
 * (see test/poll.test.ts). The browser parts (fetch, timers, the hidden-tab check)
 * come in as `PollDeps`.
 */

/** A live class, or one whose data changed in the last few minutes. */
export const LIVE_POLL_MS = 3_000;
/** A scheduled class, a quiet class, a class not published yet, or a show index during the show. */
export const WAITING_POLL_MS = 15_000;
/** A finished, quiet class, or a show index outside the show's dates. */
export const FINISHED_POLL_MS = 60_000;
/** With no change for this long, a class that claims to be live reads more slowly. */
export const QUIET_AFTER_MS = 3 * 60 * 1000;
/** How often a hidden tab checks if it is visible again. It sends no requests. */
export const HIDDEN_CHECK_MS = 1_000;
const FAILURE_RETRY_MS = 3_000;
const MAX_FAILURE_RETRY_MS = 15_000;
/** One request that takes longer than this counts as a failure. */
export const REQUEST_TIMEOUT_MS = 10_000;
/**
 * Failed reads in a row before the viewer sees "unavailable". One failed read is
 * often a network blip, and the next read succeeds.
 */
export const FAILURES_BEFORE_ERROR = 3;

const DAY_MS = 24 * 60 * 60 * 1000;

/** The part of a response that the poll loop uses. `fetch`'s Response fits. */
export interface PollResponse {
  status: number;
  ok: boolean;
  headers: { get(name: string): string | null };
  text(): Promise<string>;
}

export interface PollDeps {
  fetch: (url: string, init: { signal: AbortSignal }) => Promise<PollResponse>;
  isHidden: () => boolean;
  now: () => number;
  setTimeout: (callback: () => void, ms: number) => unknown;
  clearTimeout: (handle: unknown) => void;
}

export interface PollOptions<T> {
  url: string;
  /** How long to wait before the next read, from the latest value (null: not found). */
  intervalOf: (value: T | null) => number;
  /** Called only when the response changes. Null means "not found", which is not an error. */
  onData: (value: T | null) => void;
  /**
   * Called after `FAILURES_BEFORE_ERROR` failed reads in a row, only when no
   * value came before. Data already on the screen stays there during a failure.
   */
  onError: (error: Error) => void;
}

/** How long to wait after this many failed reads in a row. `Retry-After` can make it longer. */
export function failureDelay(failures: number, retryAfterHeader: string | null): number {
  const backoff = Math.min(FAILURE_RETRY_MS * 2 ** (failures - 1), MAX_FAILURE_RETRY_MS);
  const retryAfter = Number(retryAfterHeader);
  return Math.max(backoff, retryAfterHeader && Number.isFinite(retryAfter) ? retryAfter * 1000 : 0);
}

/** Starts reading. The function it returns stops the timer and aborts the request. */
export function poll<T>(deps: PollDeps, options: PollOptions<T>): () => void {
  let stopped = false;
  let timer: unknown;
  let controller: AbortController | undefined;
  /** The last response body. Null after a 404. Undefined before the first answer. */
  let lastText: string | null | undefined;
  let value: T | null = null;
  let first = true;
  let failures = 0;

  const schedule = (ms: number): void => {
    if (!stopped) timer = deps.setTimeout(tick, ms);
  };

  /** `parsed` is the value of `text`. The caller parses first, so a bad body never counts as seen. */
  const emit = (text: string | null, parsed: T | null): void => {
    if (text === lastText) return;
    lastText = text;
    value = parsed;
    options.onData(value);
  };

  const fail = (error: Error, retryAfter: string | null): void => {
    failures += 1;
    if (failures === FAILURES_BEFORE_ERROR && lastText === undefined) options.onError(error);
    const wait = failureDelay(failures, retryAfter);
    // Data on the screen is not urgent, so a failure does not read faster than usual.
    schedule(lastText === undefined ? wait : Math.max(wait, options.intervalOf(value)));
  };

  async function tick(): Promise<void> {
    if (stopped) return;
    if (!first && deps.isHidden()) {
      schedule(HIDDEN_CHECK_MS);
      return;
    }
    first = false;

    controller = new AbortController();
    const current = controller;
    const timeout = deps.setTimeout(() => current.abort(), REQUEST_TIMEOUT_MS);
    let text: string | null;
    let parsed: T | null = null;
    try {
      const response = await deps.fetch(options.url, { signal: current.signal });
      if (response.status === 404) {
        text = null;
      } else if (response.ok) {
        text = await response.text();
        // A body that is not JSON (a proxy or captive-portal page) is a failed read.
        parsed = JSON.parse(text) as T;
      } else {
        deps.clearTimeout(timeout);
        if (!stopped) fail(new Error(`HTTP ${response.status}`), response.headers.get('Retry-After'));
        return;
      }
    } catch (error) {
      deps.clearTimeout(timeout);
      if (!stopped) fail(error instanceof Error ? error : new Error(String(error)), null);
      return;
    }
    deps.clearTimeout(timeout);
    // An answer that arrives after stop must not reach the page.
    if (stopped) return;
    failures = 0;
    // Outside the try: an error in the page's handler is not a failed read.
    try {
      emit(text, parsed);
    } finally {
      schedule(options.intervalOf(value));
    }
  }

  void tick();
  return () => {
    stopped = true;
    deps.clearTimeout(timer);
    controller?.abort();
  };
}

/** The class fields that set the read pace. */
export interface PacedClass {
  status: string;
  rows: Array<{ inRing?: boolean }>;
}

/**
 * Decides how often a page reads a class.
 *
 * The stored flags cannot decide this alone: `status` and `inRing` can stay set
 * after a class ends. So what the page sees also counts. A class whose data
 * changes is live, whatever its flags say, and a class that stops changing slows
 * down, whatever its flags say.
 */
export function classPacer(now: () => number): (value: PacedClass | null) => number {
  let seen: PacedClass | null | undefined;
  let sawChange = false;
  let quietSince = now();

  return (value) => {
    if (value !== seen) {
      if (seen !== undefined) {
        sawChange = true;
        quietSince = now();
      }
      seen = value;
    }
    // Not published yet. Read at the waiting pace, so a new class appears soon.
    if (!value) return WAITING_POLL_MS;
    const finished = value.status === 'finished';
    if (now() - quietSince > QUIET_AFTER_MS) return finished ? FINISHED_POLL_MS : WAITING_POLL_MS;
    if (sawChange) return LIVE_POLL_MS;
    if (finished) return FINISHED_POLL_MS;
    const claimsLive = value.status === 'in_progress' || value.rows.some((row) => row.inRing === true);
    return claimsLive ? LIVE_POLL_MS : WAITING_POLL_MS;
  };
}

/** The show index fields that set the read pace. */
export interface PacedShow {
  classes: Array<{ classDate: string | null; status: string }>;
}

/**
 * The show index reads at the waiting pace from the day before its first class to
 * the day after its last, or while a class runs. At other times it reads slowly.
 */
export function showInterval(value: PacedShow | null, now: number): number {
  if (!value) return WAITING_POLL_MS;
  if (value.classes.some((cls) => cls.status === 'in_progress')) return WAITING_POLL_MS;
  const times = value.classes
    .map((cls) => (cls.classDate ? Date.parse(cls.classDate) : Number.NaN))
    .filter((time) => Number.isFinite(time));
  if (times.length === 0) return FINISHED_POLL_MS;
  const duringShow = now >= Math.min(...times) - DAY_MS && now <= Math.max(...times) + DAY_MS;
  return duringShow ? WAITING_POLL_MS : FINISHED_POLL_MS;
}
